import { useState } from 'react';
import { Button } from './ui/button';
import { Loader2, FileText, FileSpreadsheet, FileImage } from 'lucide-react';
import { toast } from 'sonner';

type ConvertFormat = 'docx' | 'xlsx' | 'pptx' | 'jpg' | 'jpeg' | 'png';

export default function PdfConverter() {
  const [isConverting, setIsConverting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const convertPdf = async (format: ConvertFormat) => {
    if (!selectedFile) {
      toast.error('Please select a PDF file first');
      return;
    }

    setIsConverting(true);
    
    try {
      toast.info(`Converting to ${format.toUpperCase()}...`);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('format', format);

      const response = await fetch('/api/convert', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Conversion failed');
      }

      // Get the filename from the Content-Disposition header or use a default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition && contentDisposition.includes('filename=')
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `converted.${format}`;

      // Get the file as a blob
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        // Reset file input
        setSelectedFile(null);
        const fileInput = document.getElementById('pdf-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }, 100);
      
      toast.success(`Successfully converted to ${format.toUpperCase()}`);
      
    } catch (error) {
      console.error('Conversion error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to convert PDF. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center">PDF Converter</h2>
      
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload PDF File
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex-1">
            <div className="flex items-center justify-center w-full px-4 py-12 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors cursor-pointer">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {selectedFile 
                    ? selectedFile.name 
                    : 'Drag and drop your PDF file here, or click to select'}
                </p>
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>
          </label>
        </div>
        {selectedFile && (
          <p className="mt-2 text-sm text-gray-500">
            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button
          variant="outline"
          className="flex flex-col items-center justify-center h-32 gap-2"
          onClick={() => convertPdf('docx')}
          disabled={!selectedFile || isConverting}
        >
          <FileText className="h-8 w-8 text-blue-600" />
          <span>Convert to Word</span>
          <span className="text-xs text-gray-500">.docx</span>
        </Button>

        <Button
          variant="outline"
          className="flex flex-col items-center justify-center h-32 gap-2"
          onClick={() => convertPdf('xlsx')}
          disabled={!selectedFile || isConverting}
        >
          <FileSpreadsheet className="h-8 w-8 text-green-600" />
          <span>Convert to Excel</span>
          <span className="text-xs text-gray-500">.xlsx</span>
        </Button>

        <Button
          variant="outline"
          className="flex flex-col items-center justify-center h-32 gap-2"
          onClick={() => convertPdf('pptx')}
          disabled={!selectedFile || isConverting}
        >
          <FileText className="h-8 w-8 text-orange-600" />
          <span>Convert to PowerPoint</span>
          <span className="text-xs text-gray-500">.pptx</span>
        </Button>

        <Button
          variant="outline"
          className="flex flex-col items-center justify-center h-32 gap-2"
          onClick={() => convertPdf('jpg')}
          disabled={!selectedFile || isConverting}
        >
          <FileImage className="h-8 w-8 text-purple-600" />
          <span>Convert to Images</span>
          <span className="text-xs text-gray-500">.jpg/.png</span>
        </Button>
      </div>

      {isConverting && (
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Converting, please wait...</span>
          </div>
        </div>
      )}
    </div>
  );
}
